import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'

import { Dialog, Button } from '@chilecompra/react-kit/components'

import { QUOTE_STATUSES_DICTIONARY_OPTIONS, QUOTE_STATUSES_PROVIDER } from '../../config/settings/constants'
import routes from '../../config/settings/routes'
import { topPage } from '../../modules/utils/topPage'
import {
  BodyParagraph,
  GridContainer,
  GridItem,
  Image,
  Root,
  Strong,
  Title
} from './QuoteSupplierActivationDialog.styles.jsx'
import { inIframe } from '../../modules/utils/inIframe.js'

/**
 * The QuoteRecordsDialog's container.
 */
const QuoteSupplierActivationDialog = props => {
  const {
    disabledInput,
    errosDisability,
    externalCode,
    handleClickDialog,
    handleOnCloseDialog,
    myPymeFileUrl,
    open,
    providerFileUrl,
    status,
    statusActive
  } = props

  const navigate = useNavigate()
  const isIframe = useMemo(() => inIframe(), [])

  const handleContinue = () => {
    disabledInput()
    if (status === QUOTE_STATUSES_DICTIONARY_OPTIONS.SAVED) {
      handleClickDialog()
      navigate({ pathname: routes.quoteStepOne, search: `?quote=${externalCode}` })
      return
    }
    errosDisability()
    navigate(routes.quoteSummary.replace(/:quoteId/, externalCode))
  }
  const handleOnClose = () => {
    handleOnCloseDialog()
  }
  const handleRedirectPyme = () => {
    window.open(myPymeFileUrl, '_blank')
  }
  const handleRedirectProviderFile = () => {
    window.open(providerFileUrl, '_blank')
  }
  useEffect(() => {
    topPage(isIframe, open)
  }, [open])

  const renderContenido = statusActive => {
    switch (statusActive) {
      case QUOTE_STATUSES_PROVIDER.DISABLED:
        return (
          <Dialog maxWidth="700px" open={open} onClose={handleOnClose} verticalAlign="top">
            <GridContainer>
              <GridItem align="center" xs={12}>
                <Image alt="proveedor deshabilitado" src="/assets/images/inhabilidad.png" />
              </GridItem>
            </GridContainer>
            <GridContainer padding="50px 0 5px">
              <GridItem align="center" xs={12}>
                <Title>
                  Tu empresa se encuentra inhábil para
                  <br />
                  participar en Mercado Público
                </Title>
              </GridItem>
            </GridContainer>
            <GridContainer padding="23px 80px 28px">
              <GridItem align="center" xs={12}>
                <BodyParagraph>
                  Nuestras fuentes oficiales registran que posees una o más causales de inhabilidad. Te recomendamos
                  revisar las causales que aplican a tu empresa para regularizar tu situación y poder enviar
                  cotizaciones en esta Compra Ágil.
                </BodyParagraph>
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 10px" container justifyContent="space-around" alignItems="center" spacing={3}>
              <GridItem sm={6} xs={12} sx={{ textAlign: { sm: 'right', xs: 'center' } }}>
                <Button color="primary" size="medium" variant="contained" onClick={handleRedirectProviderFile}>
                  Conocer detalle de inhabilidades
                </Button>
              </GridItem>
              <GridItem sm={6} xs={12} sx={{ textAlign: { sm: 'left', xs: 'center' } }}>
                <Button color="default" size="medium" variant="outlined" onClick={handleContinue}>
                  Continuar de todos modos
                </Button>
              </GridItem>
            </GridContainer>
          </Dialog>
        )
      case QUOTE_STATUSES_PROVIDER.UNACREDITED:
        return (
          <Dialog maxWidth="700px" open={open} onClose={handleOnClose} verticalAlign="top">
            <GridContainer>
              <GridItem align="center" xs={12}>
                <Image alt="proveedor sin información" src="/assets/images/habilitado.png" />
              </GridItem>
            </GridContainer>
            <GridContainer padding="50px 0 5px">
              <GridItem align="center" xs={12}>
                <Title>
                  Tu empresa no posee información sobre su estado
                  <br />
                  de habilidad
                </Title>
              </GridItem>
            </GridContainer>
            <GridContainer padding="23px 80px 28px">
              <GridItem align="center" xs={12}>
                <BodyParagraph>
                  Esta información es importante, ya que
                  <Strong>
                    &nbsp;debes estar hábil para participar de
                    <br />
                    una Compra Ágil.
                  </Strong>
                  &nbsp;Deberás obtener tu estado habilidad para conocer si tu empresa puede participar en Mercado
                  Público.
                </BodyParagraph>
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 10px" container justifyContent="space-around" alignItems="center" spacing={3}>
              <GridItem sm={6} xs={12} sx={{ textAlign: { sm: 'right', xs: 'center' } }}>
                <Button color="primary" size="medium" variant="contained" onClick={handleRedirectPyme}>
                  Obtener estado de habilidad
                </Button>
              </GridItem>
              <GridItem sm={6} xs={12} sx={{ textAlign: { sm: 'left', xs: 'center' } }}>
                <Button color="default" size="medium" variant="outlined" onClick={handleContinue}>
                  Continuar de todos modos
                </Button>
              </GridItem>
            </GridContainer>
          </Dialog>
        )
      case QUOTE_STATUSES_PROVIDER.IN_PROGRESS:
        return (
          <Dialog maxWidth="700px" open={open} onClose={handleOnClose} verticalAlign="top">
            <GridContainer>
              <GridItem align="center" xs={12}>
                <Image alt="proveedor en revisión" src="/assets/images/habilitado.png" />
              </GridItem>
            </GridContainer>
            <GridContainer padding="50px 0 5px">
              <GridItem align="center" xs={12}>
                <Title>
                  Para enviar una cotización debes estar hábil en
                  <br />
                  Mercado Público
                </Title>
              </GridItem>
            </GridContainer>
            <GridContainer padding="23px 80px 28px">
              <GridItem align="center" xs={12}>
                <BodyParagraph>
                  Tu empresa se encuentra actualmente en proceso de
                  <Strong>
                    &nbsp;revisión para obtener
                    <br />
                    &nbsp;su estado de habilidad.
                  </Strong>
                  Una vez que hayamos concluido la verificación de la información en las fuentes oficiales y confirmado
                  que tu empresa no tiene ninguna causal de inhabilidad, podrás participar en una Compra Ágil.
                </BodyParagraph>
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 10px" container justifyContent="space-around" alignItems="center" spacing={3}>
              <GridItem sm={6} xs={12} sx={{ textAlign: { sm: 'right', xs: 'center' } }}>
                <Button color="primary" size="medium" variant="contained" onClick={handleRedirectProviderFile}>
                  Dar seguimiento a la revisión
                </Button>
              </GridItem>
              <GridItem sm={6} xs={12} sx={{ textAlign: { sm: 'left', xs: 'center' } }}>
                <Button color="default" size="medium" variant="outlined" onClick={handleContinue}>
                  Continuar de todos modos
                </Button>
              </GridItem>
            </GridContainer>
          </Dialog>
        )
      default:
        return null
    }
  }
  return <Root>{renderContenido(statusActive)}</Root>
}
QuoteSupplierActivationDialog.propTypes = {
  statusActive: PropTypes.string,
  externalCode: PropTypes.string,
  status: PropTypes.string,
  open: PropTypes.bool,
  providerFileUrl: PropTypes.string,
  handleOnCloseDialog: PropTypes.func,
  handleClickDialog: PropTypes.func,
  disabledInput: PropTypes.func,
  errosDisability: PropTypes.func,
  myPymeFileUrl: PropTypes.string
}

QuoteSupplierActivationDialog.defaultProps = {
  myPymeFileUrl: '',
  statusActive: '',
  externalCode: '',
  status: '',
  open: false,
  providerFileUrl: '',
  handleOnCloseDialog: () => undefined,
  handleClickDialog: () => undefined,
  disabledInput: () => undefined,
  errosDisability: () => undefined
}
export default QuoteSupplierActivationDialog
